import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import AmexSettingsIcon from '@mui/icons-material/Settings';
import { Avatar, Link, Paper, SvgIcon, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import initials from 'initials';
import _ from 'lodash';
import pino from 'pino';
import { useCallback } from 'react';
import type { FC, HTMLAttributes, PropsWithChildren } from 'react';

import HsbcSettingsIcon from '../../../../hsbc_img/img/Settings';
import { useSessionLogoutQuery } from '../../../../queries/sessionQuery';
import { userQuery } from '../../../../queries/userQuery';
import { useZustand } from '../../../../zustandStore';

import './style.less';

const logger = pino();

type SideMenuFooterProps = HTMLAttributes<HTMLDivElement> & {
	openUserDetailsAside: () => void;
};

const SideMenuFooter: FC<PropsWithChildren<SideMenuFooterProps>> = (
	{ openUserDetailsAside = () => ({}) }: SideMenuFooterProps = {} as SideMenuFooterProps,
) => {
	const { data: user } = useQuery(userQuery);
	const logout = useSessionLogoutQuery();
	const initialsWithLimit = initials(user?.fullName || '').slice(0, 2);

	const renderUserProfilePicture = useCallback(() => {
		if (!user) {
			return null;
		}
		if (_.isEmpty(user.avatarUrl)) {
			return <Typography ts-variant="userAvatar">{initialsWithLimit}</Typography>;
		}
		return <Avatar ts-variant="circularsidebar" alt={user.fullName} src={user.avatarUrl} />;
	}, [initialsWithLimit, user]);

	return (
		<Paper ts-variant="sideMenuFooterPaper">
			<Typography component="div" sx={{ margin: '10px', cursor: 'pointer' }} onClick={openUserDetailsAside}>
				{renderUserProfilePicture()}
			</Typography>
			<Typography component="div" ts-variant="sideMenmuFooterDetailDiv">
				{user?.fullName && (
					<Typography ts-variant="imgSideSpan" title={user.fullName}>
						{user.fullName}
					</Typography>
				)}
				{user?.companyName && (
					<Typography ts-variant="imgSideSub" title={user.companyName}>
						{user.companyName}
					</Typography>
				)}
			</Typography>
			<Typography component="div" sx={{ display: 'flex', marginLeft: 'auto', alignItems: 'center' }}>
				<Link data-testid="user-settings-btn" ts-variant="sideMenuFooterLink" onClick={openUserDetailsAside}>
					{globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP ? (
						<SvgIcon
							style={{
								marginTop: '15px',
								marginLeft: '5px',
								filter: 'invert(1)',
							}}
							component={HsbcSettingsIcon}
						/>
					) : (
						<AmexSettingsIcon id="settings-icon" />
					)}
				</Link>
				<Link
					style={{
						color: 'white',
					}}
					data-testid="sideMenu-logout-btn"
					ts-variant="sideMenuFooterLink"
					onClick={async event => {
						event.preventDefault();
						try {
							await logout.mutateAsync();
							const initialState = useZustand.getInitialState();
							useZustand.setState(initialState);
						} catch (error) {
							logger.error({ message: 'Error in logging out', error });
						} finally {
							globalThis.location.href = '/signin/login';
						}
					}}
				>
					{globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP ? 'Logout' : <PowerSettingsNewIcon />}
				</Link>
			</Typography>
		</Paper>
	);
};

export default SideMenuFooter;
